import { useEffect, useRef, useState } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { aoiApi, type AoiDetailResponse, type AoiRosterDoc, type RosterRow } from '../services/aoiApi';
import { AddPhoneInline } from '../components/AddPhoneInline';
import { CopyButton } from '../components/CopyButton';
import { useRosterSelection } from '../components/useRosterSelection';
import { SelectionBar } from '../components/SelectionBar';
import { PushConfirmModal } from '../components/PushConfirmModal';
import { PushedIndicator } from '../components/PushedIndicator';
import { LeadListIndicator } from '../components/LeadListIndicator';
import { SkipMarker } from '../components/SkipMarker';
import { SortHeader, compareStrings, compareBool, type SortDir } from '../components/SortHeader';
import { HasContactIndicator } from '../components/HasContactIndicator';
import { useSkipTrace } from '../components/useSkipTrace';
import { SkipTracePanels } from '../components/SkipTracePanels';

type Tab = 'all' | 'lapsed' | 'active' | 'aggregators' | 'multi_tract';

const aggregatorTypes = new Set(['llc', 'corp', 'partnership']);

function matchTab(r: RosterRow, tab: Tab): boolean {
  if (tab === 'all') return true;
  if (tab === 'aggregators') return aggregatorTypes.has(r.entity_type);
  if (tab === 'multi_tract') return r.holdings_in_aoi >= 2;
  if (tab === 'lapsed') return !aggregatorTypes.has(r.entity_type) && r.last_lease_effective_date === null;
  if (tab === 'active') return r.last_lease_effective_date !== null;
  return true;
}

export function AoiDetail() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const [data, setData] = useState<AoiDetailResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('all');
  const [exporting, setExporting] = useState(false);
  const [exportText, setExportText] = useState<string | null>(null);
  const [unpinning, setUnpinning] = useState(false);
  const selection = useRosterSelection(id ? `aoi:${id}` : undefined);
  const [pushOpen, setPushOpen] = useState(false);

  // ── Skip-trace agent handoff (shared hook + panels) ─────────────────
  const skip = useSkipTrace(id ? `aoi:${id}` : 'aoi:none');
  // Roster sort. null field = server default (last_recording_date DESC).
  const [sortField, setSortField] = useState<'name' | 'entity_type' | 'phone' | null>(null);
  const [sortDir,   setSortDir]   = useState<SortDir>('asc');
  // Inline expand: party_id → docs (loaded lazily on first expand).
  // Status values: undefined = collapsed (not loaded), 'loading', 'error',
  // or an array (loaded). Collapsing keeps the entry in the map so a
  // re-expand is instant.
  const [docsByParty, setDocsByParty] = useState<Map<string, AoiRosterDoc[] | 'loading' | 'error'>>(new Map());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  async function toggleExpand(partyId: string) {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(partyId)) next.delete(partyId);
      else                   next.add(partyId);
      return next;
    });
    // Lazy-load on first expand only.
    if (!expanded.has(partyId) && !docsByParty.has(partyId)) {
      setDocsByParty(prev => { const m = new Map(prev); m.set(partyId, 'loading'); return m; });
      try {
        const r = await aoiApi.partyDocs(id!, partyId);
        setDocsByParty(prev => { const m = new Map(prev); m.set(partyId, r.documents); return m; });
      } catch {
        setDocsByParty(prev => { const m = new Map(prev); m.set(partyId, 'error'); return m; });
      }
    }
  }
  // Individuals-only filter, URL-persisted so back-navigation preserves.
  const [searchParams, setSearchParams] = useSearchParams();
  const individualsOnly = searchParams.get('individuals_only') === 'true';
  function toggleIndividualsOnly() {
    const next = new URLSearchParams(searchParams);
    if (individualsOnly) next.delete('individuals_only');
    else                 next.set('individuals_only', 'true');
    setSearchParams(next, { replace: false });
  }

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const r = await aoiApi.get(id);
        setData(r);
      } catch (e: any) {
        setErr(e?.message ?? 'Failed to load');
      }
    })();
  }, [id]);

  async function doExport() {
    if (!id) return;
    setExporting(true);
    try {
      const text = await aoiApi.exportDocNumbers(id);
      setExportText(text);
      try { await navigator.clipboard.writeText(text); } catch {}
    } catch (e: any) {
      setErr(e?.message ?? 'Export failed');
    } finally {
      setExporting(false);
    }
  }

  async function doUnpin() {
    if (!id) return;
    if (!confirm('Unpin this AOI? Roster data is unaffected.')) return;
    setUnpinning(true);
    try {
      await aoiApi.unpin(id);
      nav('/mi/aois');
    } catch (e: any) {
      setErr(e?.message ?? 'Unpin failed');
      setUnpinning(false);
    }
  }

  // Build the owner payload from the selected roster rows (each row carries
  // doc_numbers + TRS; county comes from the AOI) and hand it to the shared
  // skip-trace controller.
  function onSkipTrace() {
    if (!data || selection.count === 0) return;
    const rosterById = new Map(data.roster.map(r => [r.party_id, r]));
    const owners = selection.selectedRows.map(s => {
      const row = rosterById.get(s.party_id);
      return {
        name: row?.display_name_natural ?? row?.display_name ?? s.display_name,
        trs: row?.trs_keys ?? [],
        doc_numbers: row?.doc_numbers ?? [],
        county: data.aoi.county,
      };
    });
    skip.submit(data.aoi.nickname ?? data.aoi.name, owners);
  }

  // Local optimistic update of the AOI meta (nickname / county) so the
  // header + payload reflect an edit without a full refetch.
  function patchAoiMeta(patch: Partial<{ nickname: string | null; county: string | null }>) {
    setData(prev => (prev ? { ...prev, aoi: { ...prev.aoi, ...patch } } : prev));
  }

  if (err) {
    return (
      <div className="px-8 py-7 max-w-[1280px] mx-auto">
        <Link to="/mi/aois" className="text-[12px] text-text-faint font-mono hover:text-text-dim">← all AOIs</Link>
        <div className="mt-4 text-[13px] text-red bg-red/10 border border-red/40 rounded px-4 py-3">{err}</div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="px-8 py-7 max-w-[1280px] mx-auto text-[12px] text-text-faint font-mono">loading…</div>
    );
  }

  const trsLabel = data.aoi.trs.map(t => `T${t.township_num}${t.township_dir} · R${t.range_num}${t.range_dir} · Sec ${t.section}`).join(' + ');
  const rosterTabFiltered = data.roster
    .filter(r => matchTab(r, tab))
    .filter(r => !individualsOnly || r.entity_type === 'individual');
  // Apply optional click-to-sort. Server default sort (last_recording_date
  // DESC) is preserved when sortField === null.
  const rosterFiltered = sortField === null
    ? rosterTabFiltered
    : [...rosterTabFiltered].sort((a, b) => {
        if (sortField === 'name') {
          return compareStrings(
            a.display_name_natural ?? a.display_name,
            b.display_name_natural ?? b.display_name,
            sortDir,
          );
        }
        if (sortField === 'entity_type') {
          return compareStrings(a.entity_type, b.entity_type, sortDir);
        }
        return compareBool(a.has_phone, b.has_phone, sortDir);
      });

  return (
    <div className="px-8 py-7 max-w-[1280px] mx-auto">
      <Link to="/mi/aois" className="text-[12px] text-text-faint font-mono hover:text-text-dim">← all AOIs</Link>

      {/* Skip-trace panels sit at the very top so an in-flight batch can't
          be missed. Shared across AOI / lead list / party record. */}
      <SkipTracePanels skip={skip} />

      <header className="mt-4 flex items-end justify-between pb-6 border-b border-border">
        <div>
          <div className="font-display text-[28px] leading-tight flex items-center gap-3 flex-wrap">
            {(() => {
              const display = data.aoi.nickname ?? data.aoi.name;
              return display === trsLabel ? (
                <span>{trsLabel}</span>
              ) : (
                <>
                  <span>{display}</span>
                  <span className="text-text-dim text-[18px]">{trsLabel}</span>
                </>
              );
            })()}
            <InlineEdit
              value={data.aoi.nickname}
              placeholder="+ nickname"
              title="Set a display nickname (shown in the AOI list, header, and lead-list groups)"
              className="text-[12px] font-mono text-accent"
              onSave={async v => { await aoiApi.patchNickname(id!, v); patchAoiMeta({ nickname: v }); }}
            />
          </div>
          <div className="text-[12px] text-text-dim mt-2 font-mono flex items-center gap-2 flex-wrap">
            <span>{data.aoi.basin ?? 'no basin'}</span>
            <span className="text-text-ghost">·</span>
            <span>{data.counts.all} parties</span>
            <span className="text-text-ghost">·</span>
            <span className="inline-flex items-center gap-1">
              county:
              <InlineEdit
                value={data.aoi.county}
                placeholder="set county"
                title="County for the skip-trace agent owner payload"
                className="text-text"
                onSave={async v => { await aoiApi.patchCounty(id!, v); patchAoiMeta({ county: v }); }}
              />
            </span>
            <span className="text-text-ghost">·</span>
            <span>pinned {data.aoi.pinned_at?.slice(0, 10)}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={doUnpin}
            disabled={unpinning}
            className="px-3 py-2 text-[12px] text-text-dim border border-border rounded hover:text-red hover:border-red/50"
          >{unpinning ? 'Unpinning…' : 'Unpin'}</button>
          <button
            onClick={doExport}
            disabled={exporting}
            className="px-3 py-2 text-[12px] text-text-dim border border-border rounded hover:text-text hover:bg-bg-hover"
          >{exporting ? 'Exporting…' : '⤓ Export doc numbers'}</button>
          <button
            disabled
            title="session 6"
            className="px-3 py-2 text-[13px] bg-accent/30 text-text-dim font-medium rounded cursor-not-allowed"
          >Build call list →</button>
        </div>
      </header>

      {exportText !== null && (
        <div className="mt-4 bg-bg-card border border-accent/40 rounded px-4 py-3">
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-[11px] uppercase font-mono tracking-wider text-accent">Doc numbers (copied to clipboard)</div>
            <button onClick={() => setExportText(null)} className="text-text-faint hover:text-text-dim text-sm leading-none">×</button>
          </div>
          <div className="font-mono text-[12px] text-text break-all select-all">{exportText || <span className="text-text-faint">(no docs in TRS)</span>}</div>
        </div>
      )}

      <div className="mt-4 text-[11px] text-text-faint font-mono italic">
        addresses, gross acres, primary terms hydrate on-demand at build time · no bulk OCR runs
      </div>

      <div className="mt-6 grid grid-cols-[1fr_280px] gap-6">
        <div>
          <div className="flex items-center gap-1 border-b border-border">
            {(['all', 'lapsed', 'active', 'aggregators', 'multi_tract'] as Tab[]).map(t => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`px-3 py-2.5 text-[12px] font-mono uppercase tracking-wide border-b-2 ${
                  tab === t
                    ? 'text-accent-bright border-accent'
                    : 'text-text-dim border-transparent hover:text-text'
                }`}
              >
                {labelForTab(t)} <span className="text-text-faint ml-1">{data.counts[t]}</span>
              </button>
            ))}
            <button onClick={toggleIndividualsOnly}
              title="Show only entity_type = individual (hides LLCs, trusts, corps, partnerships, etc.)"
              className={
                'ml-auto px-3 py-1.5 mb-[-1px] text-[11px] font-mono rounded-t border-t border-l border-r ' +
                (individualsOnly
                  ? 'border-green/40 bg-green/10 text-green'
                  : 'border-border text-text-faint hover:text-text')
              }>
              {individualsOnly ? '✓ Individuals only' : 'Individuals only'}
            </button>
          </div>

          {/* Bulk selection actions. "Select all non-sold" picks every visible */}
          {/* row whose has_sold_minerals is false — i.e., parties who still   */}
          {/* hold an interest in this AOI. Adds to (doesn't replace) the      */}
          {/* current selection so the operator can mix with manual ticks.    */}
          {(() => {
            const nonSold = rosterFiltered.filter(r => !r.has_sold_minerals);
            const allNonSoldSelected = nonSold.length > 0 && nonSold.every(r => selection.isSelected(r.party_id));
            const soldCount = rosterFiltered.length - nonSold.length;
            return (
              <div className="mt-3 flex items-center gap-2 text-[11px] font-mono">
                <button
                  onClick={() => selection.selectMany(nonSold.map(r => ({
                    party_id: r.party_id,
                    display_name: r.display_name,
                    has_phone: r.has_phone,
                    has_address: r.has_address,
                    source_label: r.display_name,
                  })))}
                  disabled={nonSold.length === 0 || allNonSoldSelected}
                  title={
                    nonSold.length === 0
                      ? 'No non-sold parties in the current view'
                      : allNonSoldSelected
                        ? 'All non-sold parties in view are already selected'
                        : `Add ${nonSold.length} non-sold ${nonSold.length === 1 ? 'party' : 'parties'} to selection`
                  }
                  className="px-2.5 py-1 border border-accent/40 text-accent rounded hover:bg-accent-soft disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  + Select {nonSold.length} non-sold
                </button>
                {soldCount > 0 && (
                  <span className="text-text-faint">
                    excluding {soldCount} marked <span className="text-text-dim">sold</span>
                  </span>
                )}
              </div>
            );
          })()}

          <div className="mt-3">
            <div className="grid grid-cols-[26px_2fr_1.2fr_1.2fr_70px_72px_auto] items-center gap-3 px-4 py-2 text-[10px] font-mono uppercase tracking-wider text-text-faint border-b border-border">
              <div>
                <input type="checkbox"
                  checked={rosterFiltered.length > 0 && rosterFiltered.every(r => selection.isSelected(r.party_id))}
                  onChange={e => {
                    if (e.target.checked) {
                      selection.selectMany(rosterFiltered.map(r => ({
                        party_id: r.party_id,
                        display_name: r.display_name,
                        has_phone: r.has_phone,
                        has_address: r.has_address,
                        source_label: r.display_name,
                      })));
                    } else {
                      // Deselect just the visible rows.
                      for (const r of rosterFiltered) {
                        if (selection.isSelected(r.party_id)) selection.toggle({
                          party_id: r.party_id, display_name: r.display_name,
                          has_phone: r.has_phone, has_address: r.has_address,
                          source_label: r.display_name,
                        });
                      }
                    }
                  }}
                  className="accent-accent" />
              </div>
              <div>
                <SortHeader<'name' | 'entity_type' | 'phone'>
                  label="Party" field="name"
                  active={sortField} dir={sortDir}
                  onChange={(f, d) => { setSortField(f); setSortDir(d); }}
                />
              </div>
              <div>
                <SortHeader<'name' | 'entity_type' | 'phone'>
                  label="Type" field="entity_type"
                  active={sortField} dir={sortDir}
                  onChange={(f, d) => { setSortField(f); setSortDir(d); }}
                />
              </div>
              <div>Last activity</div>
              <div className="text-right">Holdings</div>
              <div>
                <SortHeader<'name' | 'entity_type' | 'phone'>
                  label="Contact" field="phone" align="right"
                  active={sortField} dir={sortDir}
                  onChange={(f, d) => { setSortField(f); setSortDir(d); }}
                />
              </div>
              <div></div>
            </div>
            {rosterFiltered.length === 0 && (
              <div className="px-4 py-8 text-center text-[12px] text-text-faint font-mono">
                no parties match this filter
                {tab === 'lapsed' || tab === 'active' ? (
                  <div className="mt-1.5 text-[11px] text-text-ghost">requires pipeline-enriched lease data</div>
                ) : null}
              </div>
            )}
            {rosterFiltered.map(r => {
              const isExpanded = expanded.has(r.party_id);
              const docsState = docsByParty.get(r.party_id);
              return (
              <div key={r.party_id} className="border-b border-border last:border-b-0">
              <div
                className="grid grid-cols-[26px_2fr_1.2fr_1.2fr_70px_72px_auto] items-center gap-3 px-4 py-3 hover:bg-bg-hover/30"
              >
                <div>
                  <input type="checkbox"
                    checked={selection.isSelected(r.party_id)}
                    onChange={() => selection.toggle({
                      party_id: r.party_id,
                      display_name: r.display_name,
                      has_phone: r.has_phone,
                      has_address: r.has_address,
                      source_label: r.display_name,
                    })}
                    className="accent-accent" />
                </div>
                <div>
                  <button
                    onClick={() => toggleExpand(r.party_id)}
                    title={isExpanded ? 'Hide documents' : 'Show documents in this AOI'}
                    className="inline-flex items-center justify-center w-4 text-text-faint hover:text-accent text-[11px] mr-0.5 align-middle"
                  >{isExpanded ? '▾' : '▸'}</button>
                  <Link to={`/mi/party/${r.party_id}`}
                    className={'text-[13px] hover:text-accent ' + (r.skipped_at ? 'text-text-faint line-through' : 'text-text')}>
                    {r.display_name_natural ?? r.display_name}
                  </Link>
                  <PushedIndicator pushedAt={r.last_pushed_to_close_at} className="ml-1.5" />
                  <LeadListIndicator status={r.lead_list_status} className="ml-1.5" />
                  <SkipMarker skippedAt={r.skipped_at} className="ml-1.5" />
                  {r.has_sold_minerals && (
                    <span
                      title="Conveyed minerals or royalty in this AOI (grantor on a mineral/royalty deed)"
                      className="ml-1.5 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider rounded bg-text-faint/15 text-text-faint align-middle"
                    >sold</span>
                  )}
                  <div className="text-[10px] text-text-faint font-mono mt-0.5 ml-5">{r.normalized_name}</div>
                </div>
                <div>
                  <span className={
                    'px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded ' +
                    (aggregatorTypes.has(r.entity_type) ? 'bg-amber/15 text-amber' :
                     r.entity_type === 'trust' ? 'bg-blue/15 text-blue' :
                     r.entity_type === 'individual' ? 'bg-green/15 text-green' :
                     'bg-text-faint/15 text-text-faint')
                  }>{r.entity_type}</span>
                </div>
                <div className="text-[12px] text-text-dim font-mono">
                  {r.last_recording_date ?? <span className="text-text-ghost italic">—</span>}
                </div>
                <div className="text-right">
                  <button
                    onClick={() => toggleExpand(r.party_id)}
                    title={isExpanded ? 'Hide documents' : `Show ${r.holdings_in_aoi} documents in this AOI`}
                    className="text-[12px] text-text font-mono hover:text-accent underline-offset-2 hover:underline"
                  >{r.holdings_in_aoi}</button>
                </div>
                <div className="flex justify-end">
                  <HasContactIndicator hasPhone={r.has_phone} hasAddress={r.has_address} />
                </div>
                <div className="flex items-center justify-end gap-1.5">
                  <CopyButton text={r.display_name_natural ?? r.display_name} />
                  {r.doc_numbers && r.doc_numbers.length > 0 && (
                    <CopyButton
                      text={r.doc_numbers.join('|')}
                      label={`${r.doc_numbers.length} docs ↗`}
                      title={`Copy ${r.doc_numbers.length} doc numbers (pipe-delimited, for Cowork)`}
                    />
                  )}
                  <AddPhoneInline partyId={r.party_id} hasPhone={r.has_phone} onSaved={async () => {
                    const fresh = await aoiApi.get(id!);
                    setData(fresh);
                  }} />
                </div>
              </div>
              {isExpanded && <PartyDocsExpansion state={docsState} />}
              </div>
              );
            })}
          </div>
        </div>

        <aside className="space-y-3">
          <MapImageCard
            aoiId={id!}
            hasImage={data.aoi.has_map_image}
            uploadedAt={data.aoi.map_image_uploaded_at}
            onChange={async () => {
              const fresh = await aoiApi.get(id!);
              setData(fresh);
            }}
          />
          <NotesCard
            aoiId={id!}
            initialNotes={data.aoi.notes}
            initialUpdatedAt={data.aoi.notes_updated_at}
          />
          <div className="bg-bg-card border border-border rounded p-4">
            <div className="text-[10px] uppercase tracking-wider font-mono text-text-faint mb-2">Claude · weekly read</div>
            <div className="text-[12px] text-text-dim italic">
              {data.aoi.claude_weekly_read ?? 'no weekly read yet · session 7 wires up the Claude pass'}
            </div>
          </div>
          {data.active_pool_orders && data.active_pool_orders.length > 0 ? (
            data.active_pool_orders.map(po => (
              <Link key={po.pool_order_id} to={`/mi/pool-orders/${po.pool_order_id}`}
                className="block bg-bg-card border border-accent/30 rounded p-4 hover:border-accent/60 hover:bg-bg-card-soft transition-colors">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-wider font-mono text-accent">Active pool order</div>
                  <span className="text-[10px] font-mono text-text-faint">{po.docket_number}</span>
                </div>
                <div className="text-[13px] text-text">
                  <strong>{po.operator ?? 'unknown operator'}</strong>
                </div>
                <div className="text-[11px] text-text-dim font-mono mt-1">
                  {po.names_on_order} names · <span className="text-accent">{po.names_matched_to_aoi} matched to your roster</span>
                </div>
                <div className="text-[10px] text-text-faint font-mono mt-2">
                  {po.filed_date ? `filed ${po.filed_date}` : ''}
                  {po.hearing_date ? ` · hearing ${po.hearing_date}` : ''}
                </div>
              </Link>
            ))
          ) : (
            <div className="bg-bg-card border border-border rounded p-4">
              <div className="text-[10px] uppercase tracking-wider font-mono text-text-faint mb-2">Pool overlap</div>
              <div className="text-[12px] text-text-ghost">no active pool order on this AOI</div>
            </div>
          )}
        </aside>
      </div>

      <SelectionBar
        selection={selection}
        sourceLabel={`AOI · ${data.aoi.nickname ?? data.aoi.name}`}
        source_kind="aoi"
        source_id={id!}
        onPush={() => setPushOpen(true)}
        onSkipTrace={onSkipTrace}
        skipTraceBusy={skip.skipBusy}
        loadToClose={skip.loadToClose}
        onLoadToCloseChange={skip.setLoadToClose}
      />

      {pushOpen && data && id && (
        <PushConfirmModal
          source_kind="aoi"
          source_id={id}
          selection={selection}
          onClose={() => setPushOpen(false)}
          onPushed={() => { selection.clear(); }}
        />
      )}
    </div>
  );
}

function labelForTab(t: Tab): string {
  if (t === 'multi_tract') return 'Multi-tract';
  return t.charAt(0).toUpperCase() + t.slice(1);
}

// ── Inline single-field editor ───────────────────────────────
// Click-to-edit text field used for the AOI nickname + county. Empty
// input clears the value (null). Saves on Enter/blur, cancels on Escape.
// onSave persists; the caller optimistically updates local state.
function InlineEdit({ value, placeholder, title, className, onSave }: {
  value: string | null;
  placeholder: string;
  title?: string;
  className?: string;
  onSave: (v: string | null) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const [busy, setBusy] = useState(false);
  useEffect(() => { setDraft(value ?? ''); }, [value]);

  async function commit() {
    const next = draft.trim() === '' ? null : draft.trim();
    if (next === (value ?? null)) { setEditing(false); return; }
    setBusy(true);
    try { await onSave(next); setEditing(false); }
    catch { /* keep the field open so the operator can retry */ }
    finally { setBusy(false); }
  }

  if (!editing) {
    return (
      <button onClick={() => setEditing(true)} title={title}
        className={'group inline-flex items-center gap-1 ' + (className ?? '')}>
        <span>{value ?? <span className="text-text-ghost italic">{placeholder}</span>}</span>
        <span className="text-text-ghost group-hover:text-text-faint text-[10px]">✎</span>
      </button>
    );
  }
  return (
    <input autoFocus value={draft} disabled={busy}
      onChange={e => setDraft(e.target.value)}
      onKeyDown={e => {
        if (e.key === 'Enter') commit();
        if (e.key === 'Escape') { setDraft(value ?? ''); setEditing(false); }
      }}
      onBlur={commit}
      placeholder={placeholder}
      className={'bg-bg-input border border-border rounded px-2 py-0.5 text-[13px] text-text ' + (className ?? '')} />
  );
}

// ── Party docs expansion ─────────────────────────────────────
// Sub-row rendered below a roster row when the operator clicks the
// chevron or holdings count. Mirrors the docs table on PartyDetail but
// scoped to the AOI's TRS set — only docs that overlap the AOI surface
// here, which is the operationally relevant slice.
function PartyDocsExpansion(props: { state: AoiRosterDoc[] | 'loading' | 'error' | undefined }) {
  const { state } = props;
  if (state === undefined || state === 'loading') {
    return (
      <div className="px-4 pb-3 pl-12 text-[11px] font-mono text-text-faint">loading documents…</div>
    );
  }
  if (state === 'error') {
    return (
      <div className="px-4 pb-3 pl-12 text-[11px] font-mono text-red">failed to load documents</div>
    );
  }
  if (state.length === 0) {
    return (
      <div className="px-4 pb-3 pl-12 text-[11px] font-mono text-text-faint italic">no documents in this AOI</div>
    );
  }
  return (
    <div className="px-4 pb-3 pl-12">
      <div className="bg-bg-card-soft border border-border rounded overflow-hidden">
        <div className="grid grid-cols-[100px_180px_2fr_120px_36px] gap-3 px-3 py-1.5 bg-bg-input/40 border-b border-border text-[10px] uppercase tracking-wider font-mono text-text-faint">
          <div>Recorded</div>
          <div>Type</div>
          <div>TRS</div>
          <div className="text-right">Doc #</div>
          <div></div>
        </div>
        {state.map(d => (
          <div key={`${d.doc_number}-${d.ordinal}`}
            className="grid grid-cols-[100px_180px_2fr_120px_36px] gap-3 items-center px-3 py-1.5 border-b border-border last:border-b-0 text-[11px] font-mono hover:bg-bg-hover/30">
            <span className="text-text-dim">{d.recording_date ?? '—'}</span>
            <span className="text-text truncate" title={d.doc_type ?? undefined}>{d.doc_type ?? '—'}</span>
            <span className="text-text-dim truncate" title={d.trs_label ?? undefined}>{d.trs_label ?? '—'}</span>
            <Link to={`/mi/document/${d.doc_number}`} className="text-accent hover:underline text-right">
              {d.doc_number}
            </Link>
            <div className="flex justify-end">
              <CopyButton text={d.doc_number} title={`Copy ${d.doc_number} to clipboard`} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Notes card ──────────────────────────────────────────────
// Free-form operator notes for the AOI. Debounced autosave: 800ms after
// the last keystroke we PATCH /api/mi/aois/:id with the current value.
// Empty string normalizes to NULL server-side, so "clear the notes" is
// the same operation as "save empty text". Status pill cycles through
// editing → saving → saved with the timestamp.
function NotesCard(props: {
  aoiId: string;
  initialNotes: string | null;
  initialUpdatedAt: string | null;
}) {
  const { aoiId, initialNotes, initialUpdatedAt } = props;
  const [value, setValue] = useState(initialNotes ?? '');
  const [status, setStatus] = useState<'idle' | 'editing' | 'saving' | 'saved' | 'error'>('idle');
  const [err, setErr] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(initialUpdatedAt);
  const timerRef = useRef<number | null>(null);
  // Track the last value we successfully saved so we don't re-PATCH on
  // initial mount or when the textarea blurs without changes.
  const lastSavedRef = useRef<string>(initialNotes ?? '');

  async function save(next: string) {
    if (next === lastSavedRef.current) return;
    setStatus('saving');
    setErr(null);
    try {
      const r = await aoiApi.patchNotes(aoiId, next);
      lastSavedRef.current = next;
      setSavedAt(r.notes_updated_at);
      setStatus('saved');
    } catch (e: any) {
      setErr(e?.message ?? 'Save failed');
      setStatus('error');
    }
  }

  function onChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    const next = e.target.value;
    setValue(next);
    setStatus('editing');
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => { void save(next); }, 800);
  }

  function onBlur() {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (value !== lastSavedRef.current) void save(value);
  }

  useEffect(() => () => {
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
  }, []);

  let statusEl: React.ReactNode = null;
  if (status === 'editing') statusEl = <span className="text-text-faint">editing…</span>;
  else if (status === 'saving') statusEl = <span className="text-accent">saving…</span>;
  else if (status === 'error') statusEl = <span className="text-red">{err ?? 'save failed'}</span>;
  else if (savedAt)            statusEl = <span className="text-text-faint">saved {savedAt.slice(0, 16).replace('T', ' ')}</span>;

  return (
    <div className="bg-bg-card border border-border rounded p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-wider font-mono text-text-faint">Notes</div>
        <div className="text-[10px] font-mono">{statusEl}</div>
      </div>
      <textarea
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        placeholder="Free-form notes — contact attempts, term hypotheses, follow-ups…"
        rows={6}
        className="w-full bg-bg text-text text-[13px] border border-border rounded px-2.5 py-2 resize-y focus:outline-none focus:border-accent/60 placeholder:text-text-faint"
      />
    </div>
  );
}

// ── Map image paste card ────────────────────────────────────
// Operator copies a map screenshot (Cowork plat / GIS / hand-drawn) and
// pastes anywhere on the card. We intercept the clipboard image, POST
// it to /api/mi/aois/:id/map-image, then re-fetch the AOI so the cached
// version of has_map_image flips and a cache-busting query string fires
// off a new GET. Paste replaces the prior image (server overwrites).
function MapImageCard(props: {
  aoiId: string;
  hasImage: boolean;
  uploadedAt: string | null;
  onChange: () => Promise<void>;
}) {
  const { aoiId, hasImage, uploadedAt, onChange } = props;
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Cache-buster keyed off uploadedAt so re-paste re-fetches the bytes.
  const [bust, setBust] = useState(0);
  const dropRef = useRef<HTMLDivElement | null>(null);

  async function uploadBlob(blob: Blob) {
    setUploading(true);
    setErr(null);
    try {
      await aoiApi.uploadMapImage(aoiId, blob);
      setBust(b => b + 1);
      await onChange();
    } catch (e: any) {
      setErr(e?.message ?? 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  function onPaste(e: React.ClipboardEvent<HTMLDivElement>) {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const it of Array.from(items)) {
      if (it.kind === 'file' && it.type.startsWith('image/')) {
        e.preventDefault();
        const blob = it.getAsFile();
        if (blob) uploadBlob(blob);
        return;
      }
    }
  }

  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file && file.type.startsWith('image/')) uploadBlob(file);
  }

  async function clearImage() {
    if (!confirm('Remove map image?')) return;
    setUploading(true);
    setErr(null);
    try {
      await aoiApi.deleteMapImage(aoiId);
      setBust(b => b + 1);
      await onChange();
    } catch (e: any) {
      setErr(e?.message ?? 'Delete failed');
    } finally {
      setUploading(false);
    }
  }

  const imgSrc = hasImage
    ? `/api/mi/aois/${aoiId}/map-image?v=${encodeURIComponent(uploadedAt ?? '')}-${bust}`
    : null;

  return (
    <div
      ref={dropRef}
      tabIndex={0}
      onPaste={onPaste}
      onDragOver={e => e.preventDefault()}
      onDrop={onDrop}
      className="bg-bg-card border border-border rounded p-3 focus:outline-none focus:border-accent/60"
      title="Click here, then paste (⌘V / Ctrl-V) a map screenshot"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-wider font-mono text-text-faint">Map</div>
        {hasImage && (
          <button
            onClick={clearImage}
            disabled={uploading}
            className="text-[10px] font-mono text-text-faint hover:text-red"
          >× remove</button>
        )}
      </div>
      {imgSrc ? (
        <>
          <img
            src={imgSrc}
            alt="AOI map"
            className="block w-full rounded border border-border"
            onError={() => setErr('Image failed to load')}
          />
          {uploadedAt && (
            <div className="text-[10px] font-mono text-text-faint mt-1.5">
              pasted {uploadedAt.slice(0, 10)}
            </div>
          )}
        </>
      ) : (
        <div className="border border-dashed border-border rounded px-3 py-6 text-center">
          <div className="text-[12px] text-text-dim">Click here, then paste a map screenshot</div>
          <div className="text-[10px] font-mono text-text-faint mt-1">⌘V / Ctrl-V · or drop an image</div>
        </div>
      )}
      {uploading && (
        <div className="text-[10px] font-mono text-accent mt-2">uploading…</div>
      )}
      {err && (
        <div className="text-[11px] text-red bg-red/10 border border-red/40 rounded px-2 py-1 mt-2">{err}</div>
      )}
    </div>
  );
}
